/**
 * `/reset` — generic, product-agnostic password-reset confirmation page.
 *
 * Cerberus sends a reset email whose link uses the calling product's
 * `return_to` origin, so the user lands here (app.cuidari.com/reset, ...)
 * carrying `token` + `user_id` in the query string.
 */
import { useEffect, useState, type FormEvent } from 'react'
import { cerberusAuthClient, CerberusError } from '@/lib/cerberus/client'

/**
 * Cerberus rejects passwords shorter than 12 chars (M2-7, same bug class as
 * the already-fixed `/entrar`). The browser `minLength` mirrors that so an
 * 8-11-char password never reaches the API and fails with an English server
 * error.
 */
const PASSWORD_MIN_LENGTH = 12

/**
 * Map a Cerberus reset failure to PT-BR copy (M2-7), mirroring `/entrar`.
 * Cerberus surfaces a machine `code` (e.g. `weak_password`) on
 * `CerberusError`; we translate the known ones and fall back to a friendly
 * generic message rather than leaking the raw English server string.
 */
function resetErrorMessage(err: unknown): string {
  if (err instanceof CerberusError) {
    switch (err.code) {
      case 'weak_password':
      case 'password_too_short':
        return `Senha muito fraca — use ao menos ${PASSWORD_MIN_LENGTH} caracteres, com letras e números.`
      case 'invalid_token':
      case 'token_expired':
        return 'Link inválido ou expirado. Solicite um novo link de redefinição.'
      default:
        if (err.status === 429) {
          return 'Muitas tentativas. Aguarde alguns instantes e tente de novo.'
        }
        if (err.status >= 500) {
          return 'Nossos servidores tropeçaram. Tente de novo em instantes.'
        }
        return 'Não foi possível redefinir sua senha. Verifique os dados e tente de novo.'
    }
  }
  if (err instanceof Error) {
    return err.message
  }
  return 'Falha ao redefinir senha'
}

function readParams() {
  if (typeof window === 'undefined') return { token: '', user_id: '' }
  const sp = new URLSearchParams(window.location.search)
  return { token: sp.get('token') ?? '', user_id: sp.get('user_id') ?? '' }
}

export function ResetPage() {
  const [params] = useState(readParams)
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    if (!success) return
    const t = setTimeout(() => {
      if (typeof window !== 'undefined') window.location.assign('/login')
    }, 1500)
    return () => clearTimeout(t)
  }, [success])

  async function onSubmit(ev: FormEvent) {
    ev.preventDefault()
    if (busy) return
    setError(null)
    if (!params.token || !params.user_id) {
      setError('Link inválido. Solicite um novo link de redefinição.')
      return
    }
    if (password.length < PASSWORD_MIN_LENGTH) {
      setError(`A senha precisa ter pelo menos ${PASSWORD_MIN_LENGTH} caracteres.`)
      return
    }
    if (password !== confirm) {
      setError('As senhas não conferem.')
      return
    }
    setBusy(true)
    try {
      await cerberusAuthClient.passwordResetConfirm({
        token: params.token,
        user_id: params.user_id,
        new_password: password,
      })
      setSuccess(true)
    } catch (err) {
      setError(resetErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-bg" data-testid="reset-page">
      <div className="w-full max-w-sm rounded-lg border border-default bg-surface p-6 shadow-sm">
        <h1 className="text-xl font-semibold mb-4">Redefinir senha</h1>
        {success ? (
          <p role="status" data-testid="reset-success" className="text-sm text-muted">
            Senha redefinida. Redirecionando…
          </p>
        ) : (
          <form onSubmit={onSubmit} className="flex flex-col gap-3">
            {error && (
              <p role="alert" data-testid="reset-error" className="rounded bg-danger/10 px-3 py-2 text-sm text-danger">
                {error}
              </p>
            )}
            <label className="flex flex-col gap-1 text-sm">
              Nova senha
              <input
                type="password" autoComplete="new-password" required minLength={PASSWORD_MIN_LENGTH}
                value={password} onChange={(e) => setPassword(e.target.value)}
                className="rounded border border-default bg-surface px-3 py-2"
                data-testid="reset-password"
              />
            </label>
            <p data-testid="reset-password-hint" className="-mt-1 text-xs text-muted">
              Pelo menos {PASSWORD_MIN_LENGTH} caracteres, com letras e números.
            </p>
            <label className="flex flex-col gap-1 text-sm">
              Confirmar nova senha
              <input
                type="password" autoComplete="new-password" required minLength={PASSWORD_MIN_LENGTH}
                value={confirm} onChange={(e) => setConfirm(e.target.value)}
                className="rounded border border-default bg-surface px-3 py-2"
                data-testid="reset-confirm"
              />
            </label>
            <button
              type="submit" disabled={busy}
              className="mt-2 rounded bg-primary px-3 py-2 text-sm font-medium text-on-primary disabled:opacity-60"
              data-testid="reset-submit"
            >
              {busy ? 'Salvando…' : 'Redefinir senha'}
            </button>
            <a className="text-xs text-muted underline" href="/login">Voltar ao login</a>
          </form>
        )}
      </div>
    </div>
  )
}
